/* eslint-disable arrow-body-style */

import React from 'react';
import classnames from 'classnames';
import { TableAddon, TableColumn } from 'tea-component';
import ReportTable from '../ReportTable';
import useDPSReportModule from '../../_hooks/useDPSReportModule';
import { getRecordKeyOfMockable } from '@gp/utils/common';
import { CARD_TITLE, buildRecord, getColumns } from './utils';
import { BoolFlag, DataType } from '@gp/enums/common';
import { MilestoneDateStatus } from '@gp/enums/milestone';

import type { IBaseProps } from '@home/types/common';
import type { IRecord, IMilestoneSimpleDTO } from '@gp/types/global';
import type {
  IDPSReportCalendarProjectSimpleVO,
  IDPSReportCalendarVO,
} from '@gp/types/dpsReport/common';

export interface ICalendarProps extends IBaseProps {
  // 标题
  title?: string;
  // 是否支持修改标题
  isSupportRenameTitle?: boolean;
  onTitleChange?: (payload: string) => void;
  // 是否loading
  isLoading?: boolean;
  // 是否表格内部loading
  isInnerLoading?: boolean;
  // Milestone是否固定支持编辑
  isMilestoneKeepEditable?: boolean;
  columnWidthMapping?: IRecord<number | string | undefined>;
  actions?: React.ReactElement | null;
  addons?: TableAddon<IDPSReportCalendarVO>[];
  projects?: IDPSReportCalendarProjectSimpleVO[] | null;
  milestonesMapping?: IRecord<IMilestoneSimpleDTO[]> | null;
  // 点击Refresh Data按钮的回调
  onRefreshData?: () => void;
  // 模块数据
  value: IDPSReportCalendarVO[];
  // 模块数据更新，删除后立马更新，新增后 需要等新增这条数据编辑完整后 才会更新，页面就不需要考虑不完整的脏数据了
  onChange?: (payload: IDPSReportCalendarVO[]) => void;
  onProjectChange?: (payload: string, cvm: IDPSReportCalendarVO) => void;
}

const Calendar: React.FC<ICalendarProps> = (props) => {
  const {
    style,
    className,
    title = CARD_TITLE,
    isSupportRenameTitle,
    onTitleChange,
    isLoading,
    isInnerLoading,
    isMilestoneKeepEditable,
    columnWidthMapping,
    actions,
    addons,
    projects,
    milestonesMapping,
    onRefreshData,
    value,
    onChange,
    onProjectChange,
  } = props;

  const { records, setRecords, onAdd, onDelete } = useDPSReportModule<IDPSReportCalendarVO>({
    buildRecord,
    value,
    onChange,
  });

  const columns: TableColumn<IDPSReportCalendarVO>[] = getColumns({
    isMilestoneKeepEditable,
    columnWidthMapping,
    projects,
    milestonesMapping,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onProjectChange: async (payload: string, cvm: IDPSReportCalendarVO) => {
      try {
        const project = projects?.find((project) => project?.project_id === payload);
        const {
          project_id: projectId,
          project: projectName,
          platform,
          image_name: imageName,
          cover,
          priority,
          business_owner: businessOwner,
          pteam,
        } = project || {};
        const recordIdx = records.findIndex((record) => record?.mock_id === cvm?.mock_id);
        const recordTmp = {
          ...records[recordIdx],
          data_type: DataType.ManualAdded,
          project_id: projectId || '',
          project: projectName || '',
          platform: platform || [],
          image_name: imageName || '',
          cover: cover || '',
          priority: priority || '',
          business_owner: businessOwner || '',
          pteam: pteam || '',
          studio_id: '',
          studio: '',
        };
        const recordsTmp = [
          ...records.slice(0, recordIdx),
          recordTmp,
          ...records.slice(recordIdx + 1),
        ];

        setRecords(recordsTmp);
        onChange?.(recordsTmp);
        onProjectChange?.(payload, cvm);
      } catch (exception) {}
    },
    onMilestoneChange: (payload: string, cvm: IDPSReportCalendarVO) => {
      const { mock_id: mockId, project_id: projectId } = cvm || {};
      const milestones = milestonesMapping?.[projectId];
      const milestone = milestones?.find((milestone) => milestone?.milestone_id === payload);
      const {
        milestone_id: milestoneId,
        milestone: milestoneName,
        date_confirm: dateConfirm,
        date,
        is_gr: isGR,
        is_launch: isLaunch,
      } = milestone || {};
      const recordIdx = records.findIndex((record) => record?.mock_id === mockId);
      const recordTmp = {
        ...records[recordIdx],
        milestone_id: milestoneId || '',
        milestone: milestoneName || '',
        date_confirm: dateConfirm ?? MilestoneDateStatus.Confirm,
        date: date || '',
        is_gr: isGR || BoolFlag.False,
        is_launch: isLaunch || BoolFlag.False,
      };
      const recordsTmp = [
        ...records.slice(0, recordIdx),
        recordTmp,
        ...records.slice(recordIdx + 1),
      ];
      setRecords(recordsTmp);
      onChange?.(recordsTmp);
    },
    onChange: (cvm: IDPSReportCalendarVO, key, payload) => {
      const recordIdx = records.findIndex((item) => {
        const { mock_id: mockId } = item || {};
        return mockId === cvm?.mock_id;
      });
      const recordTmp = {
        ...records[recordIdx],
        [key]: payload || '',
      };
      const recordsTmp = [
        ...records.slice(0, recordIdx),
        recordTmp,
        ...records.slice(recordIdx + 1),
      ];
      setRecords(recordsTmp);
      onChange?.(recordsTmp);
    },
  });

  return (
    <ReportTable
      showRefreshData
      style={style}
      className={classnames('dbg-dps-report__calendars-table-card', className)}
      title={title}
      isSupportRenameTitle={isSupportRenameTitle}
      onTitleChange={onTitleChange}
      isLoading={isLoading}
      isInnerLoading={isInnerLoading}
      // addBtnTips=""
      actions={actions}
      addons={addons}
      columns={columns}
      records={records}
      recordKey={getRecordKeyOfMockable}
      onAdd={onAdd}
      onDelete={onDelete}
      onRefreshData={onRefreshData}
    />
  );
};

export default Calendar;
